import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Check,
  MessagesSquare,
  ChevronDown,
} from "lucide-react";

import {
  listThreads,
  renameThread,
  deleteThread,
} from "../../../../api/threadsChat";
import { usePortalTarget } from "./sidebarUtils";
import { setPendingThreadSelect } from "../../../Center/Threads/pendingSelect";
import { useLayout } from "../../../contexts/LayoutContext";
import { useSearchRegistry } from "../../Search/SearchRegistry";

const SELECT_EVENT = "sidebar:threads-thread-selected";

// Rows revealed per "Load more" click (and the initial window).
const PAGE_STEP = 5;

// Threads are served newest-first (`last_msg_at` desc) — this list never
// re-sorts, so the order is exactly the server's deterministic order.
const formatRelativeTime = (ts) => {
  if (!ts) return "";
  // The API may hand back unix seconds, unix millis, or an ISO string; all
  // three land on the same wall clock here.
  const num = typeof ts === "number" ? ts : Number(ts);
  let ms;
  if (Number.isFinite(num) && num > 0) {
    ms = num > 1e12 ? num : num * 1000;
  } else {
    ms = Date.parse(ts);
    if (Number.isNaN(ms)) return "";
  }
  const diff = Math.floor((Date.now() - ms) / 1000);
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
};

/**
 * Thread history for the /threads chat page, as the "Chat" nav section's
 * dropdown — Feed/Reports-style portal into `threads-portal-slot` (or the
 * navbar slot when the sidebar is closed). Shows PAGE_STEP rows at a time
 * behind a "Load more" row; no internal scrollbar — the nav tree scrolls.
 *
 * Replaces the old always-open ThreadsSidebarList that was pinned below the
 * nav tree.
 */
const ThreadsSidebarPortal = () => {
  const portalTarget = usePortalTarget("threads-portal-slot");
  const navigate = useNavigate();
  const { currentAppPage, leftOpen } = useLayout();

  const [threads, setThreads] = useState([]);
  const [nextOffset, setNextOffset] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_STEP);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [renamingThreadId, setRenamingThreadId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [pendingDeleteThreadId, setPendingDeleteThreadId] = useState(null);

  // Refresh replaces the list with the first server page. `visibleCount` is
  // deliberately left alone — the render slice caps it, so a shrunken result
  // can't overrun and an expanded window survives ordinary refreshes.
  const loadThreads = useCallback(async () => {
    try {
      const { threads: rows, next_offset } = await listThreads();
      setThreads(rows);
      setNextOffset(next_offset);
    } catch (e) {
      console.error("Error loading threads:", e);
      setThreads([]);
      setNextOffset(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  // Mount fetch (Feed/Reports do the same): the ⌘K registry and the navbar
  // breadcrumb need the titles even while the dropdown is collapsed. Refresh
  // stays event-driven — no polling, ever.
  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    const handler = () => loadThreads();
    window.addEventListener("threads:changed", handler);
    return () => window.removeEventListener("threads:changed", handler);
  }, [loadThreads]);

  /* ── Selection mirror ── the page selects too (⌘K, lazy create), so the
     active row follows the same event this list dispatches. */
  useEffect(() => {
    const handler = (e) => {
      if (e.detail && "threadId" in e.detail)
        setCurrentThreadId(e.detail.threadId);
    };
    window.addEventListener(SELECT_EVENT, handler);
    return () => window.removeEventListener(SELECT_EVENT, handler);
  }, []);

  useEffect(() => {
    if (currentAppPage !== "/threads" && currentThreadId !== null) {
      setCurrentThreadId(null);
    }
  }, [currentAppPage, currentThreadId]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section && e.detail.section !== "threads")
        setCurrentThreadId(null);
    };
    window.addEventListener("sidebar:active-section-changed", handler);
    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, []);

  /* ── Selection dispatch ────────────────────────────────────────────────────
     MainThreads subscribes to SELECT_EVENT in a mount effect. Selecting from
     another route therefore can't dispatch here at all: the routes sit inside
     <AnimatePresence mode="wait">, so the page only mounts after the old
     route's exit animation — well past any timeout scheduled off the
     currentAppPage flip (the previous approach; it lost this race and cost a
     second click). Cross-route selections go through pendingSelect.js
     instead, consumed by the page's own mount effect. */
  const dispatchSelect = (threadId) => {
    window.dispatchEvent(
      new CustomEvent(SELECT_EVENT, { detail: { threadId } }),
    );
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "threads" },
      }),
    );
  };

  /* ── Actions ────────────────────────────────────────────────────────────── */
  // A "new thread" is a DRAFT: nothing is created server-side until the first
  // send, so an opened-and-abandoned thread never reaches the list.
  const handleSelect = (threadId) => {
    setCurrentThreadId(threadId);
    setPendingDeleteThreadId(null);

    if (currentAppPage !== "/threads") {
      setPendingThreadSelect(threadId);
      navigate("/threads");
      return;
    }
    dispatchSelect(threadId);
  };

  // Reveal-then-fetch: the buffer (first server page, 30 rows) empties before
  // the network is touched again, so most clicks are instant.
  const handleLoadMore = async () => {
    if (visibleCount < threads.length) {
      setVisibleCount((c) => c + PAGE_STEP);
      return;
    }
    if (nextOffset === null) return;
    try {
      const { threads: more, next_offset } = await listThreads(nextOffset);
      setThreads((prev) => [...prev, ...more]);
      setNextOffset(next_offset);
      setVisibleCount((c) => c + PAGE_STEP);
    } catch (e) {
      console.error("Error loading more threads:", e);
    }
  };

  const commitRename = async (threadId) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingThreadId(null);
      return;
    }
    try {
      await renameThread(threadId, trimmed);
      // Optimistic: the row shows the new title before the refetch lands, and
      // the page's header (which shows the same title) hears the same event.
      setThreads((prev) =>
        prev.map((t) =>
          t.thread_id === threadId ? { ...t, title: trimmed } : t,
        ),
      );
      window.dispatchEvent(new CustomEvent("threads:changed"));
    } catch (err) {
      console.error("Rename thread failed:", err);
    } finally {
      setRenamingThreadId(null);
    }
  };

  const confirmDelete = async (threadId) => {
    try {
      await deleteThread(threadId);
      setThreads((prev) => prev.filter((t) => t.thread_id !== threadId));
      // Deleting the open thread drops the page back to a draft.
      if (currentThreadId === threadId) handleSelect(null);
      window.dispatchEvent(new CustomEvent("threads:changed"));
    } catch (err) {
      console.error("Delete thread failed:", err);
    } finally {
      setPendingDeleteThreadId(null);
    }
  };

  /* ── Breadcrumb (navbar) ───────────────────────────────────────────────────
     Same contract as ReportsSidebarPortal. addKind is "threads", NOT "chat" —
     NavBreadcrumb special-cases the literal string "chat" into the legacy
     Private/Public/Team button cluster. */
  const emitThreadsBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/threads") return;
    const activeThread = threads.find((t) => t.thread_id === currentThreadId);
    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/threads",
          subLabel: activeThread ? activeThread.title || "New thread" : null,
          subIcon: null,
          hasAdd: true,
          addKind: "threads",
          addLabel: "New thread",
        },
      }),
    );
  }, [threads, currentThreadId, currentAppPage]);

  useEffect(() => {
    emitThreadsBreadcrumb();
  }, [emitThreadsBreadcrumb, leftOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/threads") emitThreadsBreadcrumb();
    };
    window.addEventListener("breadcrumb:request", handler);
    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitThreadsBreadcrumb]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section !== "/threads") return;
      handleSelect(null);
    };
    window.addEventListener("breadcrumb:add", handler);
    return () => window.removeEventListener("breadcrumb:add", handler);
    // handleSelect closes over currentAppPage; re-bind when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAppPage]);

  /* ── Search registry (kept minimal; the ⌘K menu is being revised later) ──
     Registers the FULL fetched list, not the visible window — search should
     find threads the dropdown hasn't revealed yet. */
  const { register, unregister } = useSearchRegistry();
  useEffect(() => {
    const items = [
      {
        id: "threads-new",
        label: "New thread",
        keywords: ["new", "chat", "thread", "ask"],
        icon: Plus,
        iconColor: "#0EA5E9",
        event: {
          navigateTo: "/threads",
          name: SELECT_EVENT,
          detail: { threadId: null },
        },
      },
      ...threads.map((t) => ({
        id: `thread-${t.thread_id}`,
        label: t.title || "New thread",
        keywords: ["thread", "chat"],
        icon: MessagesSquare,
        event: {
          navigateTo: "/threads",
          name: SELECT_EVENT,
          detail: { threadId: t.thread_id },
        },
      })),
    ];

    register("/threads", items);
    return () => unregister("/threads");
  }, [threads, register, unregister]);

  /* ── Render ─────────────────────────────────────────────────────────────── */
  const isInNavbar = portalTarget?.dataset?.navbar === "true";

  const visibleThreads = threads.slice(0, visibleCount);
  const hasMore = visibleCount < threads.length || nextOffset !== null;

  const content = (
    <div
      className="flex flex-col gap-y-px"
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── New thread ── deliberately subdued: styled like a plain list row,
          not a call-to-action (birr's preference). Hidden in the navbar
          dropdown, matching Feed/Reports (the breadcrumb "+" covers it). */}
      {!isInNavbar && (
        <button
          onClick={() => handleSelect(null)}
          className="group w-full flex items-center gap-x-2 rounded-md px-2 py-1.5 text-[12px] text-mutedText hover:text-mainText hover:bg-mainButtonBg/50 transition-colors"
          title="New thread"
        >
          <Plus className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span>New thread</span>
        </button>
      )}

      {/* ── Threads (served newest-first; never re-sorted here) ── */}
      {visibleThreads.map((t) => {
        const isActive = t.thread_id === currentThreadId;
        const isRenaming = renamingThreadId === t.thread_id;
        const isPendingDelete = pendingDeleteThreadId === t.thread_id;
        const title = t.title || "New thread";

        if (isRenaming) {
          return (
            <div
              key={t.thread_id}
              className="flex items-center gap-x-1 px-2 py-1"
            >
              <input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename(t.thread_id);
                  if (e.key === "Escape") setRenamingThreadId(null);
                }}
                onBlur={() => commitRename(t.thread_id)}
                className="flex-1 min-w-0 bg-transparent text-[12px] text-mainText border border-velBlue/30 rounded px-1.5 py-1 outline-none focus:border-velBlue/60"
              />
            </div>
          );
        }

        return (
          <div key={t.thread_id} className="group relative shrink-0">
            <button
              onClick={() => handleSelect(t.thread_id)}
              title={title}
              className={[
                "w-full text-left rounded-md pl-2 pr-14 py-1.5 text-[12px] transition-colors flex items-center",
                isActive
                  ? "text-velBlue bg-velBlue/[0.08] font-medium"
                  : "text-mutedText hover:text-mainText hover:bg-mainButtonBg/50",
              ].join(" ")}
            >
              <span className="truncate flex-1 min-w-0">{title}</span>
            </button>

            {/* ── End-of-row slot: timestamp and rename/delete ALTERNATE in
                the same spot — time when idle, actions on hover. */}
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-mutedText/60 pointer-events-none group-hover:opacity-0 group-focus-within:opacity-0 transition-opacity">
              {formatRelativeTime(t.last_msg_at)}
            </span>

            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-x-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <button
                onClick={() => {
                  setPendingDeleteThreadId(null);
                  setRenamingThreadId(t.thread_id);
                  setRenameValue(title);
                }}
                className="p-1 rounded text-mutedText hover:text-mainText hover:bg-mainButtonBg/60 transition-colors"
                title="Rename"
                aria-label="Rename thread"
              >
                <Pencil className="h-3 w-3" strokeWidth={2} />
              </button>
              {/* Two-click delete: the trash arms, the check commits. No
                  window.confirm — it would blur the sidebar and freeze the
                  whole app on a list action. */}
              <button
                onClick={() => {
                  if (isPendingDelete) confirmDelete(t.thread_id);
                  else setPendingDeleteThreadId(t.thread_id);
                }}
                onBlur={() => {
                  if (isPendingDelete) setPendingDeleteThreadId(null);
                }}
                className={[
                  "p-1 rounded transition-colors",
                  isPendingDelete
                    ? "text-red-500 bg-red-500/10"
                    : "text-mutedText hover:text-red-500 hover:bg-mainButtonBg/60",
                ].join(" ")}
                title={isPendingDelete ? "Confirm delete" : "Delete"}
                aria-label={isPendingDelete ? "Confirm delete" : "Delete thread"}
              >
                {isPendingDelete ? (
                  <Check className="h-3 w-3" strokeWidth={2.5} />
                ) : (
                  <Trash2 className="h-3 w-3" strokeWidth={2} />
                )}
              </button>
            </div>
          </div>
        );
      })}

      {loaded && threads.length === 0 && (
        <div className="px-2 py-1.5 text-[11px] text-mutedText/60">
          No threads yet
        </div>
      )}

      {hasMore && (
        <button
          onClick={handleLoadMore}
          className="group w-full flex items-center gap-x-2 rounded-md px-2 py-1.5 text-[12px] text-mutedText/70 hover:text-mainText hover:bg-mainButtonBg/50 transition-colors"
          title="Load more threads"
        >
          <ChevronDown className="h-3 w-3 shrink-0" strokeWidth={2} />
          <span>Load more</span>
        </button>
      )}
    </div>
  );

  if (!portalTarget) return null;
  return createPortal(content, portalTarget);
};

export default ThreadsSidebarPortal;
